import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import logo from '../../../assets/logo.png'
import { useAuthStore } from '../store/authStore'

const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Za-z]/, 'Password must include at least one letter')
      .regex(/\d/, 'Password must include at least one number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

export function ResetPasswordPage(): JSX.Element {
  const resetPassword = useAuthStore((s) => s.resetPassword)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const isDev = import.meta.env.DEV
  const resetBaseUrl = import.meta.env.VITE_PORTAL_RESET_PASSWORD_URL as string | undefined

  let resetBaseOrigin = ''
  try {
    resetBaseOrigin = resetBaseUrl ? new URL(resetBaseUrl).origin : ''
  } catch {
    resetBaseOrigin = ''
  }

  const showDevResetWarning =
    isDev && !!resetBaseOrigin && resetBaseOrigin !== window.location.origin

  const [serverError, setServerError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  })

  const onSubmit = async (formData: ResetPasswordFormData): Promise<void> => {
    setServerError('')
    setSuccessMessage('')

    if (!token) {
      setServerError('Reset token is missing. Please open the reset link from your email again.')
      return
    }

    const result = await resetPassword(token, formData.newPassword)

    if (!result.success) {
      setServerError(result.error || 'Unable to reset password.')
      return
    }

    setSuccessMessage('Password reset successfully. Redirecting to login...')
    setTimeout(() => {
      navigate('/login', { replace: true })
    }, 1200)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src={logo} alt="QuickComm" className="h-10 w-auto" />
        </div>

        <div className="rounded-xl border border-[#E5E7EB] bg-white p-8 shadow-sm">
          <h1 className="mb-1 text-[28px] font-bold leading-tight text-gray-900">
            Reset password
          </h1>
          <p className="mb-6 text-[14px] text-gray-500">
            Set a new password for your account.
          </p>

          {showDevResetWarning && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
              Reset links are configured for <strong>{resetBaseOrigin}</strong>, but you opened this app on{' '}
              <strong>{window.location.origin}</strong>. Update backend reset base URL for local testing.
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label
                htmlFor="newPassword"
                className="mb-1.5 block text-[13px] font-medium text-gray-700"
              >
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                {...register('newPassword')}
                className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 text-[14px] text-gray-900 outline-none transition-colors focus:border-brand-green"
                placeholder="NewPass123"
              />
              {errors.newPassword && (
                <p className="mt-1 text-xs text-red-600">{errors.newPassword.message}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="mb-1.5 block text-[13px] font-medium text-gray-700"
              >
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                {...register('confirmPassword')}
                className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 text-[14px] text-gray-900 outline-none transition-colors focus:border-brand-green"
                placeholder="NewPass123"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            {successMessage && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                <p className="text-[13px] text-green-700">{successMessage}</p>
              </div>
            )}

            {serverError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-[13px] text-red-600">{serverError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="h-10 w-full rounded-lg bg-brand-green text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Resetting...' : 'Reset password'}
            </button>

            <p className="text-center text-[13px] text-gray-500">
              <Link to="/login" className="font-medium text-brand-green hover:underline">
                Back to sign in
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}
